// 앱의 단축 메뉴를 화면의 두 자리로 나눈다 — 전역은 작성칸 곁, 글 단축은 글의 손.
//
// 화면(`@maru/ui`)은 열쇠 한 줄만 들고 다닌다. 열쇠는 `{앱}/{callback_id}` 다 — 앱 번호는
// UUID 라 빗금이 없으므로 첫 빗금에서 가르면 `callback_id` 에 빗금이 있어도 되찾는다.

import type { Command, Shortcut, ShortcutEntry } from '@maru/client'
import { type Candidate, locale_now, msg, type ShortcutItem, translate } from '@maru/ui'

export function shortcut_key(e: { app: string; callback_id: string }): string {
  return `${e.app}/${e.callback_id}`
}

export function from_key(key: string): { app: string; callback_id: string } | null {
  const at = key.indexOf('/')
  if (at <= 0 || at === key.length - 1) return null
  return { app: key.slice(0, at), callback_id: key.slice(at + 1) }
}

/** ⚡ 가 뜨는 자리 — 작성칸과 글 줄. 여기에 손이 처음 닿을 때 단축 메뉴를 받는다. */
const SHORTCUT_SPOTS = '.Composer, .MessageRow'

/** 사건의 대상이 ⚡ 가 뜨는 자리 안인가. 요소가 아니면(창·문서) 거짓. */
export function near_shortcuts(target: unknown): boolean {
  const closest = (target as { closest?: unknown } | null)?.closest
  if (typeof closest !== 'function') return false
  return closest.call(target, SHORTCUT_SPOTS) !== null
}

/** 커맨드 명단을 `/` 자동완성 후보로. 거둔 것은 뺀다. */
export function command_candidates(list: readonly Command[]): Candidate[] {
  return list
    .filter((c) => !c.revoked)
    .map((c) => ({ id: c.id, name: c.name, hint: c.description }))
}

/** 메뉴를 두 자리로. 순서는 서버가 준 그대로다(앱 이름·적힌 순서). */
export function split_shortcuts(menu: readonly ShortcutEntry[]): {
  globals: ShortcutItem[]
  messages: ShortcutItem[]
} {
  const item = (e: ShortcutEntry): ShortcutItem => ({
    key: shortcut_key(e),
    name: e.name,
    description: e.description,
    app_name: e.app_name,
  })
  return {
    globals: menu.filter((e) => e.kind === 'global').map(item),
    messages: menu.filter((e) => e.kind === 'message').map(item),
  }
}

/**
 * 앱 등록 칸의 한 줄을 단축 목록으로. `;` 로 나누고 한 개는 `종류:callback_id:이름[:설명]` —
 * 종류는 `전역`·`글`(또는 `global`·`message`). 설명을 비우면 이름을 쓴다. 틀리면 던진다 —
 * 조용히 빼면 관리자는 달았다고 믿는다. 한도(종류마다 다섯)는 서버가 본다.
 */
export function parse_shortcuts(text: string): Shortcut[] {
  return text
    .split(';')
    .map((part) => part.trim())
    .filter((part) => part !== '')
    .map((part) => {
      const [kind_word = '', callback_id = '', name = '', ...rest] = part
        .split(':')
        .map((x) => x.trim())
      const kind =
        kind_word === '전역' || kind_word === 'global'
          ? 'global'
          : kind_word === '글' || kind_word === 'message'
            ? 'message'
            : null
      if (kind === null || callback_id === '' || name === '')
        throw new Error(
          translate(
            locale_now(),
            msg(
              '바로 가기를 읽지 못했어요: "{part}". 종류:callback_id:이름[:설명] 모양으로 적어 주세요.',
            ),
            { part },
          ),
        )
      const description = rest.join(':').trim()
      return { kind, callback_id, name, description: description === '' ? name : description }
    })
}
